// import { isEmpty } from 'lodash';
import { Link, Translation } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import { Header } from '@swf/core/enums';
import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { Icon } from '@swf/sports/components';
import { ExpandablePanel, Title } from '@swf/sports/components/ExpandablePanel/ExpandablePanel';
import GroupedTemplatePanels from '@swf/sports/components/GroupedTemplatePanels/GroupedTemplatePanels';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import { configuredLandingPageEventTypeIds } from '@swf/sports/pages/config';

import {
  TXTWCL_All_Competitions,
  TXTWCL_Upcoming___N0
} from './SportsUpcomingEventsWidget.messages.json';
import styles from './SportsUpcomingEventsWidget.scss';
import LoadingState from './components/LoadingState/LoadingState';
import { sportsUpcomingEventsWidgetModel, useSportsUpcomingEventsWidget } from './model';

const WidgetContent = () => {
  const { widgetSelect, widgetDispatch } = useSportsUpcomingEventsWidget();
  const marketIds = useSelector(widgetSelect.marketIds);
  const groups = useSelector(widgetSelect.groups);
  const eventType = useSelector(widgetSelect.firstEventType);
  const isEventTypeLandingPage = useSelector((state: RootState) =>
    select.sports.isEventTypeLandingPage(state, {
      eventTypeId: eventType?.eventTypeId,
      configuredLandingPageEventTypeIds
    })
  );

  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);

  if (!eventType) {
    return null;
  }

  const { eventTypeId, eventTypeSlug, name } = eventType;

  return (
    <>
      <Vary by={[Header.TimeZone]} />
      <ExpandablePanel
        title={
          <Title isNested>
            <Translation {...TXTWCL_Upcoming___N0} values={{ 0: name }} />
          </Title>
        }
        isNested
      >
        <GroupedTemplatePanels groupedOn="COMPETITION" groups={groups} isNested />
        <Link
          className={styles.allInPlayButton}
          alias={isEventTypeLandingPage ? 'sportsCompetitions' : 'sportsSport'}
          values={{ eventTypeId, eventTypeSlug }}
        >
          <Translation {...TXTWCL_All_Competitions} />
          <Icon name="chevron-small-right" size="xl" />
        </Link>
      </ExpandablePanel>
    </>
  );
};

const SportsUpcomingEventsWidget: WidgetFC = () => {
  const { widgetSelect } = useSportsUpcomingEventsWidget();
  const isLoading = useSelector(widgetSelect.isLoading);
  const hasPreloaded = useSelector(widgetSelect.hasPreloaded);
  const hasErrored = useSelector(widgetSelect.hasErrored);
  const events = useSelector(widgetSelect.events);

  if (isLoading && !hasPreloaded) {
    return <LoadingState />;
  }

  if (hasErrored || !Object.keys(events).length) {
    return null;
  }

  return <WidgetContent />;
};

SportsUpcomingEventsWidget.model = sportsUpcomingEventsWidgetModel;

export default withWidgetTestId(SportsUpcomingEventsWidget, 'sports-upcoming-events', {
  className: styles.wrapper
});
